import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CalendarX, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmtFrDate = (iso) => {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
};

const daysBetween = (start, end) => {
  const out = [];
  if (!start || !end) return out;
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  if (isNaN(s) || isNaN(e) || s > e) return out;
  const cur = new Date(s);
  while (cur <= e) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

export default function SettingsPanel() {
  const [s, setS] = useState(null);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);

  const save = async () => {
    try {
      const payload = {
        phone: s.phone, email: s.email, address: s.address,
        tva_takeaway: Number(s.tva_takeaway),
        orders_enabled: !!s.orders_enabled, reservations_enabled: !!s.reservations_enabled,
        closed_dates: sortedUnique(s.closed_dates || []),
      };
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Erreur"); }
  };

  const sortedUnique = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();

  const addRange = () => {
    if (!rangeStart) {
      toast.error("Sélectionnez au moins une date de début");
      return;
    }
    const end = rangeEnd || rangeStart;
    if (end < rangeStart) {
      toast.error("La date de fin doit être après le début");
      return;
    }
    const days = daysBetween(rangeStart, end);
    const merged = sortedUnique([...(s.closed_dates || []), ...days]);
    setS({ ...s, closed_dates: merged });
    toast.success(`${days.length} jour${days.length > 1 ? "s" : ""} ajouté${days.length > 1 ? "s" : ""}`);
    setRangeStart("");
    setRangeEnd("");
  };

  const removeDate = (d) => {
    setS({ ...s, closed_dates: (s.closed_dates || []).filter((x) => x !== d) });
  };

  const closed = sortedUnique(s?.closed_dates || []);
  // group consecutive dates into ranges for display
  const groupedRanges = (() => {
    const groups = [];
    for (const d of closed) {
      if (!d) continue;
      const last = groups[groups.length - 1];
      if (last) {
        const nextExpected = new Date(new Date(last[last.length - 1] + "T00:00:00").getTime() + 86400000).toISOString().slice(0, 10);
        if (d === nextExpected) { last.push(d); continue; }
      }
      groups.push([d]);
    }
    return groups;
  })();

  if (!s) return <div className="text-muted-foreground">Chargement…</div>;

  return (
    <div data-testid="settings-panel" className="max-w-3xl">
      <h1 className="font-display text-4xl mb-2">Paramètres</h1>
      <p className="text-muted-foreground mb-8">Coordonnées du restaurant, vacances et activation des modules.</p>

      <div className="bg-white border border-border rounded-sm p-6 mb-6">
        <h2 className="font-display text-2xl mb-4">Restaurant</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Téléphone"><Input value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} data-testid="settings-phone" /></Field>
          <Field label="Email"><Input value={s.email || ""} onChange={(e) => setS({ ...s, email: e.target.value })} data-testid="settings-email" /></Field>
          <div className="sm:col-span-2"><Field label="Adresse"><Input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} data-testid="settings-address" /></Field></div>
          <Field label="TVA emporter (%)"><Input type="number" step="0.1" value={s.tva_takeaway} onChange={(e) => setS({ ...s, tva_takeaway: Number(e.target.value) })} /></Field>
        </div>
        <div className="flex flex-wrap gap-6 mt-6">
          <label className="flex items-center gap-3"><Switch checked={s.orders_enabled} onCheckedChange={(v) => setS({ ...s, orders_enabled: v })} data-testid="toggle-orders" /> Commandes en ligne activées</label>
          <label className="flex items-center gap-3"><Switch checked={s.reservations_enabled} onCheckedChange={(v) => setS({ ...s, reservations_enabled: v })} data-testid="toggle-reservations" /> Réservations activées</label>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm p-6 mb-6">
        <h2 className="font-display text-2xl mb-2 flex items-center gap-2"><CalendarX size={22} /> Vacances / fermetures exceptionnelles</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Durant ces jours, <strong>les commandes à emporter et les réservations sont bloquées</strong>.
          Ajoutez une période entière (début → fin) ou une seule journée.
        </p>

        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end p-4 bg-secondary/30 rounded-sm mb-4">
          <Field label="Date de début">
            <Input type="date" min={today} value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} data-testid="range-start" className="rounded-sm" />
          </Field>
          <Field label="Date de fin (optionnelle)">
            <Input type="date" min={rangeStart || today} value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} data-testid="range-end" className="rounded-sm" />
          </Field>
          <button
            type="button"
            onClick={addRange}
            data-testid="add-range-btn"
            className="bg-primary text-white px-4 py-2 h-10 rounded-sm hover:bg-primary/90 flex items-center gap-2 justify-center"
          >
            <Plus size={16} /> Ajouter
          </button>
        </div>

        {groupedRanges.length === 0 ? (
          <div className="text-sm text-muted-foreground italic text-center py-6 border border-dashed border-border rounded-sm">
            Aucune fermeture programmée.
          </div>
        ) : (
          <div className="space-y-2" data-testid="closed-dates-list">
            {groupedRanges.map((grp, i) => {
              const isRange = grp.length > 1;
              const label = isRange
                ? `Du ${fmtFrDate(grp[0])} au ${fmtFrDate(grp[grp.length - 1])} (${grp.length} jours)`
                : fmtFrDate(grp[0]);
              return (
                <div key={i} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-sm">
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarX size={14} className="text-amber-800 shrink-0" />
                    <span className="font-medium text-amber-900">{label}</span>
                  </div>
                  <div className="flex gap-1">
                    {isRange && (
                      <button
                        type="button"
                        onClick={() => grp.forEach(removeDate)}
                        className="text-destructive text-xs hover:underline"
                        data-testid={`remove-range-${i}`}
                      >Retirer toute la période</button>
                    )}
                    {!isRange && (
                      <button
                        type="button"
                        onClick={() => removeDate(grp[0])}
                        className="text-destructive hover:bg-destructive/10 p-1.5 rounded-sm"
                        aria-label="Retirer cette date"
                        data-testid={`remove-date-${grp[0]}`}
                      ><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button onClick={save} data-testid="save-settings-btn" className="bg-primary text-white px-6 py-3 rounded-sm hover:bg-primary/90">Enregistrer</button>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">{label}</Label>{children}</div>;
}
